import {Stack, useNotify, useViewport} from '@treatwell/ui';
import {useTranslation} from 'react-i18next';
import {ProposedService} from '../../../server/routes/availability/types';
import {useAppDispatch, useAppSelector} from '../../hooks/store';
import {
  fetchApiAvailability,
  resetAvailability,
} from '../../store/slices/availability/slice';
import {
  dispatchMultiEmployeeSelect,
  dispatchStartTimeSelect,
} from '../../store/slices/dispatch';
import {
  selectServices,
  selectShowMultiStaffSelection,
  selectSkus,
} from '../../store/slices/selectors';
import {getUrlInfo} from '../../utils/getUrlInfo';
import {Item, OrderItem} from '../OrderItem/OrderItem';
import {trackRemoveService, trackSelectEmployee} from '../../utils/gtmTracking';
import {selectVenueId} from '../../store/slices/venue/slice';
import {updateLocalStorage} from '../../utils/updateLocalStorage';

interface Props {
  items: Item[];
  onRemove: (skuId: string) => void;
  isMultiEmployeeSelectEnabled?: boolean;
  onEmployeeSelect: ({
    menuItemId,
    employeeId,
  }: {
    menuItemId: string;
    employeeId: string;
  }) => void;
}

export const OrderList = (props: Props) => {
  const isMobile = useViewport({device: 'mobile'});

  return (
    <Stack
      as="ul"
      data-cy="OrderList"
      space={
        props.isMultiEmployeeSelectEnabled && !isMobile ? 'base' : undefined
      }
    >
      {props.items.map((item) => (
        <li key={item.id}>
          <OrderItem
            onEmployeeSelect={props.onEmployeeSelect}
            {...item}
            onRemove={props.onRemove}
            isMultiEmployeeSelectEnabled={props.isMultiEmployeeSelectEnabled}
          />
        </li>
      ))}
    </Stack>
  );
};

export const OrderListContainer = () => {
  const {notifyError} = useNotify();
  const {t} = useTranslation();
  const services = useAppSelector(selectServices);
  const skus = useAppSelector(selectSkus);
  const dispatch = useAppDispatch();
  const venueId = useAppSelector(selectVenueId);
  const items = services.map((service) => {
    const selectedSku = service.skuIds.map((selectedSkuId) => {
      return skus[selectedSkuId];
    });
    return {
      id: service.id,
      name: service.name,
      skus: selectedSku,
    };
  });
  const isMultiEmployeeSelectEnabled = useAppSelector(
    selectShowMultiStaffSelection,
  );

  const onEmployeeSelect = ({
    menuItemId,
    employeeId,
  }: {
    menuItemId: string;
    employeeId: string;
  }) => {
    trackSelectEmployee(employeeId, menuItemId);

    dispatchMultiEmployeeSelect({
      menuItemId,
      employeeId,
    });
  };
  const removeSku = ({skuId}: {skuId: string}) => {
    const {url, searchParams} = getUrlInfo(location.href);
    const proposedServices = searchParams.get('proposedServices');
    if (!proposedServices) {
      return;
    }

    trackRemoveService(skuId);

    const filteredProposedServices = JSON.parse(
      decodeURIComponent(proposedServices),
    )
      .map((service: ProposedService) => {
        const optionIds = service.optionIds.filter((option) => {
          return option !== skuId;
        });
        if (optionIds.length) {
          return {
            menuItemId: service.menuItemId,
            optionIds,
            employeeId: service.employeeId,
          };
        }
      })
      .filter(Boolean);

    updateLocalStorage(venueId, filteredProposedServices);

    if (filteredProposedServices.length) {
      url.searchParams.set(
        'proposedServices',
        JSON.stringify(filteredProposedServices),
      );
      history.pushState(null, '', url.toString());
      dispatch(
        fetchApiAvailability({
          url: url.toString(),
          resetToInitialDate: true,
        }),
      )
        .unwrap()
        .then(() => {
          dispatchStartTimeSelect({
            startTime: null,
            hasDiscount: false,
            totalPrice: '',
          });
        })
        .catch(() => {
          notifyError(t('error.header'));
        });
    } else {
      url.searchParams.delete('proposedServices');
      url.searchParams.delete('date');
      url.searchParams.delete('timeFrom');
      history.pushState(null, '', url.toString());
      dispatch(resetAvailability());
    }
  };

  return (
    <OrderList
      onEmployeeSelect={onEmployeeSelect}
      items={items}
      onRemove={(skuId) => {
        removeSku({skuId});
      }}
      isMultiEmployeeSelectEnabled={isMultiEmployeeSelectEnabled}
    />
  );
};
